import { DATE_FORMAT_DATE, DATE_FORMAT_DATETIME } from '_core/dates/formats';
import { FinalForm } from '_core/final-form/finalForm';
import { BaseForm } from '_core/forms/baseForm';
import { FormErrors } from '_core/forms/formErrors';
import { Checkbox } from '_core/inputs/checkbox';
import { DateInputInFormGroup } from '_core/inputs/dateInput';
import { FormGroupForFinalForm } from '_core/inputs/formGroup';
import { InputGroupForFinalForm } from '_core/inputs/inputGroup';
import {
  InputMoneyInFormGroup,
  InputMoneyInFormGroupForFinalForm,
} from '_core/inputs/inputMoney';
import { Select } from '_core/inputs/select';
import { formatMoney } from '_core/money/formatMoney';
import { Link } from '_core/router5/link';
import { LinkButton } from '_core/router5/linkButton';
import {
  FormNavigationLock,
  IFormNavigationLock,
} from '_core/router5/navigationLock';
import { Toolbar } from '_core/toolbar';
import {
  Button,
  Callout,
  FormGroup,
  H4,
  InputGroup,
  Intent,
  Menu,
  MenuItem,
  Popover,
  Position,
} from '@blueprintjs/core';
import dayjs from 'dayjs';
import { ExpeditionRequestsAutocompleteInFormGroup } from 'expeditionRequests/autocomplete';
import {
  expeditionTransportationAccountingStateOptions,
  ExpeditionTransportationGroup,
  getExpeditionTransportationGroupLabel,
  IExpeditionTransportation,
} from 'expeditionTransportations/types';
import { SubmissionErrors } from 'final-form';
import arrayMutators from 'final-form-arrays';
import { Col, Grid, Row, VGrid } from 'layout/contentLayout';
import * as React from 'react';
import { useRef } from 'react';
import { StationsAutocompleteInFormGroup } from 'stations/autocomplete';
import { TechrunTransportationState } from 'techrunTransportations/types';
import { transportationStateOptions } from 'transportations/types';

import {
  ExpeditionTransportationExpensesField,
  IExpeditionTransportationExpensesFieldItem,
} from './expensesField';
import {
  ExpeditionTransportationIncomesField,
  IExpeditionTransportationIncomesFieldItem,
} from './incomesField';

export interface IExpeditionTransportationFormValues {
  expenses: IExpeditionTransportationExpensesFieldItem[];
  income: IExpeditionTransportationIncomesFieldItem[];
  note: string;
  tariffCalculated: string | null;
}

enum RateType {
  Day = 'DAY',
  Ton = 'TON',
  Wagon = 'WAGON',
}

const rateTypeOptions = [RateType.Wagon, RateType.Ton, RateType.Day].map(
  rateType => ({
    label: {
      [RateType.Day]: 'За сутки',
      [RateType.Wagon]: 'За вагон',
      [RateType.Ton]: 'За тонну',
    }[rateType],
    value: rateType,
  })
);

interface IProps {
  initialValues: IExpeditionTransportationFormValues;
  navigationLock: IFormNavigationLock;
  transportation: IExpeditionTransportation;
  onConfirmActiveDislocation: () => Promise<SubmissionErrors | void>;
  onConfirmDepartureDate: () => Promise<SubmissionErrors | void>;
  onMoveToGroup: (
    group: ExpeditionTransportationGroup
  ) => Promise<SubmissionErrors | void>;
  onRejectActiveDislocation: () => Promise<SubmissionErrors | void>;
  onRejectDepartureDate: () => Promise<SubmissionErrors | void>;
  onSave: (
    values: IExpeditionTransportationFormValues
  ) => Promise<SubmissionErrors | void>;
  onSaveAndContinue: (
    values: IExpeditionTransportationFormValues
  ) => Promise<SubmissionErrors | void>;
}

export function ExpeditionTransportationForm({
  initialValues,
  navigationLock,
  transportation,
  onConfirmActiveDislocation,
  onConfirmDepartureDate,
  onMoveToGroup,
  onRejectActiveDislocation,
  onRejectDepartureDate,
  onSave,
  onSaveAndContinue,
}: IProps) {
  const submitHandlerRef = useRef(onSave);

  return (
    <FinalForm
      initialValues={initialValues}
      mutators={{ ...arrayMutators }}
      onSubmit={values => submitHandlerRef.current(values)}
    >
      {({ dirty, error, form, handleSubmit, submitError, submitting }) => (
        <BaseForm onSubmit={handleSubmit}>
          <FormNavigationLock
            navigationLock={navigationLock}
            shouldLock={dirty}
          />

          <FormErrors error={error || submitError} />

          <VGrid>
            {transportation.group === ExpeditionTransportationGroup.New && (
              <Row>
                <Callout
                  icon="eye-open"
                  intent={Intent.SUCCESS}
                  title="Это новая отправка"
                >
                  <Toolbar>
                    <Popover
                      content={
                        <Menu>
                          {transportation.groupsTransitionRoute.map(group => (
                            <MenuItem
                              key={group}
                              text={getExpeditionTransportationGroupLabel(
                                group
                              )}
                              onClick={() => {
                                submitHandlerRef.current = () =>
                                  onMoveToGroup(group);
                                form.submit();
                              }}
                            />
                          ))}
                        </Menu>
                      }
                      disabled={submitting}
                      position={Position.BOTTOM_RIGHT}
                    >
                      <Button
                        disabled={submitting}
                        rightIcon="caret-down"
                        text="Переместить"
                      />
                    </Popover>
                  </Toolbar>
                </Callout>
              </Row>
            )}

            {transportation.isDepartureDateChanged && (
              <Row>
                <Callout
                  icon="warning-sign"
                  intent={Intent.WARNING}
                  title={`Дата отправления изменилась с ${dayjs(
                    transportation.departureDate
                  ).format(DATE_FORMAT_DATE)} на ${dayjs(
                    transportation.departureDateNext
                  ).format(DATE_FORMAT_DATE)}`}
                >
                  <Toolbar>
                    <Button
                      intent={Intent.PRIMARY}
                      text="Подтвердить"
                      disabled={submitting}
                      onClick={() => {
                        submitHandlerRef.current = onConfirmDepartureDate;
                        form.submit();
                      }}
                    />

                    <Button
                      intent={Intent.DANGER}
                      text="Отклонить"
                      disabled={submitting}
                      onClick={() => {
                        submitHandlerRef.current = onRejectDepartureDate;
                        form.submit();
                      }}
                    />
                  </Toolbar>
                </Callout>
              </Row>
            )}

            {transportation.isWaybillNumberChanged && (
              <Row>
                <Callout
                  icon="warning-sign"
                  intent={Intent.WARNING}
                  title={`Номер накладной изменился с ${transportation.dislocationActive.waybillNumber} на ${transportation.dislocationNext.waybillNumber}`}
                >
                  <p>
                    <Link
                      params={{
                        dislocationDatetime:
                          transportation.dislocationCreated.created,
                        number: transportation.wagon,
                      }}
                      to="wagons.edit.history"
                    >
                      История дислокаций
                    </Link>
                  </p>

                  <Toolbar>
                    <Button
                      intent={Intent.PRIMARY}
                      text="Подтвердить"
                      disabled={submitting}
                      onClick={() => {
                        submitHandlerRef.current = onConfirmActiveDislocation;
                        form.submit();
                      }}
                    />

                    <Button
                      intent={Intent.DANGER}
                      text="Отменить"
                      disabled={submitting}
                      onClick={() => {
                        submitHandlerRef.current = onRejectActiveDislocation;
                        form.submit();
                      }}
                    />
                  </Toolbar>
                </Callout>
              </Row>
            )}

            <Row>
              <VGrid>
                <Row>
                  <Grid>
                    <Col span={4}>
                      <H4>Основная информация</H4>

                      <FormGroup
                        label="Наименование отправки"
                        labelFor="transportationName"
                      >
                        <InputGroup
                          id="transportationName"
                          name="transportationName"
                          readOnly
                          value={transportation.transportationName}
                        />
                      </FormGroup>

                      <Grid>
                        <Col span={2}>
                          <FormGroup label="Накладная" labelFor="waybillNumber">
                            <InputGroup
                              id="waybillNumber"
                              name="waybillNumber"
                              readOnly
                              value={transportation.waybillNumber}
                            />
                          </FormGroup>
                        </Col>

                        <Col span={2}>
                          <FormGroup label="Вагон" labelFor="wagon">
                            <InputGroup
                              id="wagon"
                              name="wagon"
                              readOnly
                              value={transportation.wagon}
                            />
                          </FormGroup>
                        </Col>
                      </Grid>

                      <FormGroup>
                        <Toolbar>
                          <LinkButton
                            intent={Intent.SUCCESS}
                            params={{ number: transportation.wagon }}
                            rel="noopener"
                            target="_blank"
                            text="Дислокация"
                            to="wagons.edit.history"
                          />
                        </Toolbar>
                      </FormGroup>

                      <ExpeditionRequestsAutocompleteInFormGroup
                        id="request"
                        label="Заявка"
                        name="request"
                        readOnly
                        value={transportation.request}
                      />

                      <FormGroup label="Грузоотправитель" labelFor="consigner">
                        <InputGroup
                          id="consigner"
                          name="consigner"
                          readOnly
                          value={transportation.consigner ?? ''}
                        />
                      </FormGroup>

                      <FormGroup label="Плательщик" labelFor="payer">
                        <InputGroup
                          id="payer"
                          name="payer"
                          readOnly
                          value={transportation.payer}
                        />
                      </FormGroup>

                      <Grid>
                        <Col span={2}>
                          <InputMoneyInFormGroupForFinalForm
                            id="tariffCalculated"
                            label="Расчётный тариф"
                            name="tariffCalculated"
                          />
                        </Col>

                        <Col span={2}>
                          <InputMoneyInFormGroup
                            id="tariffIvc"
                            label="Тариф из дислокации"
                            name="tariffIvc"
                            readOnly
                            value={transportation.tariffIvc ?? ''}
                          />
                        </Col>
                      </Grid>

                      <InputMoneyInFormGroup
                        id="tariffOverride"
                        label="Тариф из перечня"
                        name="tariffOverride"
                        readOnly
                        value={transportation.tariffOverride ?? ''}
                      />

                      <Grid>
                        <Col span={2}>
                          <FormGroup label="Вид ставки">
                            <Select
                              disabled
                              fill
                              id="sellRateType"
                              name="sellRateType"
                              options={rateTypeOptions}
                              value={
                                transportation.sellRateType as unknown as RateType
                              }
                            />
                          </FormGroup>
                        </Col>

                        <Col span={2}>
                          <FormGroup
                            label="Вес груза (тн)"
                            labelFor="cargoWeight"
                          >
                            <InputGroup
                              id="cargoWeight"
                              name="cargoWeight"
                              readOnly
                              value={transportation.cargoWeight}
                            />
                          </FormGroup>
                        </Col>
                      </Grid>

                      <Checkbox
                        checked={transportation.isEmpty}
                        disabled
                        id="isEmpty"
                        label="Порожняя отправка"
                        name="isEmpty"
                      />
                    </Col>

                    <Col span={4}>
                      <H4>Даты и станции</H4>

                      <Grid>
                        <Col span={2}>
                          <DateInputInFormGroup
                            fill
                            id="departureDate"
                            label="Дата отправления"
                            name="departureDate"
                            readOnly
                            value={transportation.departureDate}
                          />
                        </Col>

                        <Col span={2}>
                          <DateInputInFormGroup
                            fill
                            id="arrivalDate"
                            label="Дата прибытия"
                            name="arrivalDate"
                            readOnly
                            value={transportation.arrivalDate}
                          />
                        </Col>
                      </Grid>

                      <StationsAutocompleteInFormGroup
                        id="departureStation"
                        label="Станция отправления"
                        name="departureStation"
                        readOnly
                        value={transportation.departureStation}
                      />

                      <StationsAutocompleteInFormGroup
                        id="arrivalStation"
                        label="Станция назначения"
                        name="arrivalStation"
                        readOnly
                        value={transportation.arrivalStation}
                      />

                      <StationsAutocompleteInFormGroup
                        id="operationStation"
                        label="Станция операции"
                        name="operationStation"
                        readOnly
                        value={transportation.operationStation}
                      />

                      <DateInputInFormGroup
                        fill
                        id="departureAfterUnload"
                        label="Дата отправки вагона после выгрузки"
                        name="departureAfterUnload"
                        readOnly
                        value={transportation.departureAfterUnload}
                      />

                      <DateInputInFormGroup
                        fill
                        id="arrivalBeforeLoad"
                        label="Дата прибытия вагона под погрузку"
                        name="arrivalBeforeLoad"
                        readOnly
                        value={transportation.arrivalBeforeLoad}
                      />
                    </Col>

                    <Col span={4}>
                      <H4>Редактировать</H4>

                      <FormGroup label="Состояние">
                        <Select
                          disabled
                          fill
                          id="state"
                          name="state"
                          options={transportationStateOptions}
                          value={
                            transportation.state as unknown as TechrunTransportationState
                          }
                        />
                      </FormGroup>

                      <FormGroup label="Бух. состояние">
                        <Select
                          disabled
                          fill
                          id="accountingState"
                          name="accountingState"
                          options={
                            expeditionTransportationAccountingStateOptions
                          }
                          value={transportation.accountingState}
                        />
                      </FormGroup>

                      <FormGroupForFinalForm
                        label="Заметка"
                        labelFor="note"
                        name="note"
                      >
                        <InputGroupForFinalForm id="note" name="note" />
                      </FormGroupForFinalForm>

                      <Grid>
                        <Col span={2}>
                          <DateInputInFormGroup
                            dateFormat={DATE_FORMAT_DATETIME}
                            id="created"
                            label="Дата создания"
                            name="created"
                            readOnly
                            value={transportation.created}
                          />
                        </Col>

                        <Col span={2}>
                          <DateInputInFormGroup
                            dateFormat={DATE_FORMAT_DATETIME}
                            id="modified"
                            label="Дата изменения"
                            name="modified"
                            readOnly
                            value={transportation.modified}
                          />
                        </Col>
                      </Grid>
                    </Col>
                  </Grid>
                </Row>

                <Row>
                  <H4>Доходы</H4>

                  <ExpeditionTransportationIncomesField
                    change={form.change}
                    name="income"
                    shipments={transportation.shipments}
                  />
                </Row>

                <Row>
                  <H4>Расходы</H4>
                  <ExpeditionTransportationExpensesField
                    change={form.change}
                    name="expenses"
                  />
                </Row>

                <Row>
                  <H4>
                    Прибыль:{' '}
                    {formatMoney(transportation.profit, { withCurrency: true })}
                  </H4>
                </Row>
              </VGrid>
            </Row>

            <Row stickToBottom>
              <Toolbar align="right">
                <Button
                  disabled={submitting}
                  text="Сохранить и продолжить редактирование"
                  onClick={() => {
                    submitHandlerRef.current = onSaveAndContinue;
                    form.submit();
                  }}
                />

                <Button
                  disabled={submitting}
                  intent={Intent.PRIMARY}
                  text="Сохранить"
                  type="submit"
                  onClick={() => {
                    submitHandlerRef.current = onSave;
                  }}
                />
              </Toolbar>
            </Row>
          </VGrid>
        </BaseForm>
      )}
    </FinalForm>
  );
}
